'use client';

import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api, getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Maximize2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { isUtc, zoneAbbreviation } from '@/lib/dateFormat';
import type {
  BlockType, TimelineBlock, TimelineLane, CoverageTimelineView,
} from '@/types/coverage-timeline';
import { formatDuration } from './coverageFormatters';

/**
 * THE ROTATION TIMELINE — a Gantt-style read of the coverage plan (V1, forward-looking).
 *
 * One lane per drone in the rotation. ACTUAL bars (`source: 'actual'`) come from real
 * `mission_segments` history; every other bar is FORECAST — projected in steady state from the
 * standing replacement forecast, capped at one rotation lap / 4 hours. See
 * `docs/PLANS/coverage-rotation-timeline/IMPLEMENTATION.md` for the full reasoning, including the
 * review findings this file was rewritten against (D8 in that record).
 *
 * ~~FETCHED ON DEMAND~~ **ALWAYS ON.** It used to sit behind a "Show plan" button, on the
 * reasoning that a derived, moderately expensive read should not fire on every mission-detail
 * visit. It does not: this component mounts only inside the coverage panel's EXPANDED body, and
 * that panel is collapsed by default — so the read fires when an operator opens continuous
 * coverage, which is the moment they are asking this question. What the button actually cost was
 * that the schedule was two clicks away and both of them came after the rotation was already
 * committed: an operator could commit an unattended multi-hour rotation without ever being shown
 * what it would do.
 *
 * ~~No live socket either~~ — it refreshes off the coverage/rotation frames the panel already
 * subscribes to, passed down as a `revision` counter rather than a second socket. A plan does not
 * need sub-second updates, but it does need to stop being stale the moment the planner republishes.
 *
 * WHY BLOCK IDENTITY DOES NOT LEAN ON COLOR OR BORDER ALONE (product review #2/#3). A block only
 * a few pixels wide — a 4-minute transit on a 4-hour window is a handful of pixels — cannot carry
 * legible in-bar text, and a dashed-vs-solid border is easy to miss at that width too. So there
 * are THREE independent, width-independent cues, not one: (1) a static LEGEND above the chart
 * that decodes every color and the actual/forecast border regardless of any bar's width, (2) a
 * background wash over the whole forecast region (right of the NOW line), not just per block, and
 * (3) the in-bar label and dashed border, which remain as reinforcement where there is room.
 * Every block is also focusable with a full `aria-label`, so the exact same information is never
 * ONLY available to a sighted mouse user hovering a `title`.
 */

/**
 * The shapes moved to `@/types/coverage-timeline` (PR 3, S2) so the attach wizard's client-side
 * projection can PRODUCE this view as well as this component rendering it. A pure move - the
 * definitions are unchanged.
 */

const BLOCK_LABEL: Record<BlockType, string> = {
  on_station: 'ON STATION',
  transit: 'TRANSIT',
  standby: 'STANDBY',
  charging: 'CHARGING',
  swap: 'SWAP',
};

const LEGEND_ORDER: BlockType[] = ['on_station', 'transit', 'charging', 'swap', 'standby'];

// Status hues, by name (military-ui skill) — never a hex literal in the component.
const BLOCK_CLASS: Record<BlockType, string> = {
  on_station: 'bg-status-green/25 border-status-green-marker text-status-green-fg',
  transit: 'bg-status-blue/25 border-status-blue-marker text-status-blue-fg',
  standby: 'bg-status-gray/20 border-status-gray-marker text-status-gray-fg',
  charging: 'bg-status-amber/25 border-status-amber-marker text-status-amber-fg',
  swap: 'bg-status-amber/25 border-status-amber-marker text-status-amber-fg',
};

/**
 * CHARGING and SWAP share one hue (both are "recovering at the dock") but are operationally
 * different — a swap-capable dock is minutes away, a charging one may be much longer (product
 * review #9). `currentColor` picks up the block's own `text-status-amber-fg`, so the hatch tints
 * with the theme rather than a hardcoded value.
 */
const SWAP_HATCH_STYLE: CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, currentColor 3px, currentColor 4px)',
  backgroundBlendMode: 'multiply',
};

/**
 * Can this block carry its own name at this width?
 *
 * PER LABEL, because one number for all five is wrong at both ends: `SWAP` needs about 30 px and
 * `ON STATION` about 70, so a single threshold either clips the long ones (there is no ellipsis on
 * a Gantt bar — it just cuts, and `ON STATI` reads as a rendering fault) or hides the short ones
 * where there was room. Roughly 6.2 px per uppercase tracking-wider character at 9 px, 7.6 at 11,
 * plus the bar's own horizontal padding.
 */
function labelFitsPx(type: BlockType, expanded: boolean): number {
  return BLOCK_LABEL[type].length * (expanded ? 7.6 : 6.2) + 10;
}

/** How far back the visible window reaches at most, regardless of how old the fetched history is. */
const LOOKBACK_MS = 2 * 60 * 60_000;

/** The shortest interval between two socket-driven re-reads of a moderately expensive query. */
const REFRESH_FLOOR_MS = 5_000;

/** And the longest a drawn plan may go unread, since nothing on this chart advances by itself. */
const MAX_STALENESS_MS = 60_000;

/**
 * EVERY ASYNC OPERATION HAS A TIMEOUT, and a LATCHED one needs it most.
 *
 * `inFlight` exists so a burst of frames coalesces into one trailing read, and it is cleared only
 * in that read's `finally`. Without a bound on the request, a single one that never settles — a
 * stalled connection through nginx, a tablet changing network — leaves the latch set for the life
 * of the component: the staleness interval, every revision bump and the operator's own Refresh all
 * return at the guard, and Refresh returns BEFORE it can even mark itself busy, so the press
 * changes nothing on screen. `stale` stays false and `error` stays null, so the chart looks live
 * while the panel's own 15 s poll keeps moving beside it — the two-statements-disagreeing failure
 * the staleness ceiling exists to prevent, arrived at from the other side.
 *
 * The same bound, and the same reasoning, as the achieved read's `ACHIEVED_REQUEST_TIMEOUT_MS`.
 */
const TIMELINE_REQUEST_TIMEOUT_MS = 30_000;

/**
 * THE AXIS STEPS AN OPERATOR ALREADY READS CLOCKS IN. Never 7 or 13 minutes: a Gantt is read by
 * landing on a label and counting, and that only works when the labels are on the minutes people
 * round to.
 */
const TICK_STEPS_MS = [1, 2, 5, 10, 15, 20, 30, 60, 120, 180, 240, 360].map((m) => m * 60_000);

/** No two axis labels closer than this. Below it they collide and the ruler becomes a smear. */
export const MIN_TICK_GAP_PX = 76;

/** Rendered widths of the two axis labels, in the monospace they are drawn in. */
const TICK_LABEL_PX = 32; // "14:15"
const NOW_LABEL_PX = 58; // "NOW 14:15"
const TICK_LABEL_GUTTER_PX = 8;

/** The widths the chart is actually asked to work at: inline in the panel, and expanded. */
const MIN_TRACK_PX = 440;
const MIN_TRACK_EXPANDED_PX = 720;

/**
 * How coarse the ruler has to be at this width — the whole point of measuring.
 *
 * Tuned once for the inline case, a 4-hour window either prints three labels at full screen or
 * overlapping ones at 440 px. The step is chosen from how many labels FIT, so the same chart reads
 * at both.
 */
export function tickStepMs(spanMs: number, trackPx: number): number {
  const slots = Math.max(2, Math.floor(trackPx / MIN_TICK_GAP_PX));
  const target = spanMs / slots;
  return TICK_STEPS_MS.find((s) => s >= target) ?? TICK_STEPS_MS[TICK_STEPS_MS.length - 1];
}

/**
 * Ticks on a round step, not on the window's own start — 14:00, 14:15, never 14:03.
 *
 * Aligned to the epoch, so an org whose timezone is a whole number of hours off UTC gets labels on
 * the hour and an org on a half-hour offset gets them on the half hour. Still round, still evenly
 * spaced, just not the same round numbers.
 *
 * THE CAP IS NOT DEAD CODE. The server clamps its horizon to four hours, but `drawable()` exists
 * on the premise that the body is not trusted, and an absurd `horizonEndsAt` would otherwise turn
 * this into hundreds of thousands of DOM nodes — a hang rather than a throw, which is the one
 * failure mode that guard does not catch.
 */
const MAX_TICKS = 400;

export function axisTicks(startMs: number, endMs: number, stepMs: number): number[] {
  const out: number[] = [];
  for (let t = Math.ceil(startMs / stepMs) * stepMs; t <= endMs; t += stepMs) {
    out.push(t);
    if (out.length >= MAX_TICKS) break;
  }
  return out;
}

/**
 * The chart's own rendered width.
 *
 * Feature-detected like every other self-sizing surface in this app (`CopDockRoster`, `DroneMap`):
 * a runtime without `ResizeObserver` keeps the fallback width rather than taking the mission page
 * down on mount. The observer fires once on `observe`, so there is no synchronous measure here.
 */
function useTrackWidth(fallbackPx: number): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallbackPx);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || fallbackPx));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallbackPx]);
  return [ref, width];
}

/** Bare "HH:MM" in the org's display timezone, no zone suffix — every caller here is either
 *  a dense repeated axis label or a per-block accessible name, both of which would only get
 *  clutter, not clarity, from a suffix repeated on every value (the same reasoning as
 *  `dateFormat.ts`'s `formatClock`). */
function zonedHHMM(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
}

/**
 * Where a block's OWN close is unknown, this decides where to draw its right edge — a
 * presentation call, deliberately kept out of the pure service function it reads from (which
 * only ever says what it actually knows). An open ACTUAL bar is "ongoing as of now, unless a
 * later block in this same lane already says what came next"; an open FORECAST bar (a queued
 * STANDBY with nothing scheduled) draws to the edge of the visible window.
 *
 * EXPORTED because the COP's collapsed ribbon (`_cop/copCoverageRibbon.ts`) draws the SAME blocks
 * and must close them the same way. It got this wrong on its first pass - running an open actual
 * `on_station` to the window edge - and the two surfaces then answered "is the station manned at
 * 08:40" differently from one payload, with the smaller one giving the optimistic answer. One
 * function, or they drift again.
 */
export function resolveBlockEnd(block: TimelineBlock, laneBlocks: TimelineBlock[], now: number, windowEnd: number): number {
  if (block.endsAt !== null) return new Date(block.endsAt).getTime();
  if (block.source === 'actual') {
    const startMs = new Date(block.startsAt).getTime();
    const later = laneBlocks
      .filter((b) => b !== block && new Date(b.startsAt).getTime() > startMs)
      .map((b) => new Date(b.startsAt).getTime());
    return later.length > 0 ? Math.min(...later) : now;
  }
  return windowEnd;
}

function blockAriaLabel(block: TimelineBlock, callsign: string | null, timezone: string): string {
  const range = `${zonedHHMM(block.startsAt, timezone)} to ${block.endsAt ? zonedHHMM(block.endsAt, timezone) : 'open'}`;
  // THE DURATION IS HERE BECAUSE THE STRIP IS NOT ANNOUNCED. `TimelineBlockDetail` deliberately
  // carries no live region — the name below is what a screen reader hears on activation — so a
  // fact that lived only in the strip was a fact a screen reader could never reach. Product
  // review, 2026-09-07.
  const seconds = blockSeconds(block);
  return `${callsign ?? 'unknown drone'} — ${BLOCK_LABEL[block.type]}, `
    + `${block.source === 'actual' ? 'actual' : 'forecast'}, ${range}`
    + `${seconds === null ? '' : `, ${formatDuration(seconds)}`}`;
}

/** A block's own length, or `null` when it is open-ended or its stamps will not parse. */
function blockSeconds(block: TimelineBlock): number | null {
  if (block.endsAt === null) return null;
  const startMs = new Date(block.startsAt).getTime();
  const endMs = new Date(block.endsAt).getTime();
  // A bar whose times will not parse still draws (see `drawable` — only render-fatal fields are
  // validated), so neither the name nor the strip may turn a NaN into a confident duration.
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return Math.max(0, Math.round((endMs - startMs) / 1000));
}

/**
 * IDENTITY FOR A BAR THAT HAS NO ID OF ITS OWN.
 *
 * `TimelineBlock` carries no key — only the one block per chart that stands for the current
 * rotation action has an `actionId` (`coverage-timeline.ts:523`, `cycle === 0`). Selection has to
 * survive a re-read, and the chart re-reads itself every time the rotation moves, so the key is
 * derived from what the SERVER said: `mission_rotation_members` is `UNIQUE (mission_id, drone_id)`
 * and a lane's blocks are ordered and non-overlapping, so drone + type + start is unique.
 *
 * NO RENDER INDEX IN HERE. An earlier cut appended `i` and it defeated the entire purpose: a
 * routine handover re-projects the lane from the current cycle, so the same bar shifts index and
 * the selection silently cleared while the bar was still drawn — on the common path, since the
 * re-read happens BECAUSE the plan moved. Standby bars were worse: `coverage-timeline.ts:580`
 * stamps them `startsAt: now`, so their key changes on every read regardless. Engineering review,
 * 2026-09-07. React's own `key` still gets the index appended, because siblings need uniqueness
 * where selection needs stability, and those are not the same requirement.
 */
function blockKey(lane: TimelineLane, block: TimelineBlock): string {
  return `${lane.droneId}:${block.type}:${block.startsAt}`;
}

/**
 * IS THIS BODY SOMETHING THE CHART CAN ACTUALLY DRAW?
 *
 * Every field checked here is one that throws in RENDER if it is wrong, not one that merely looks
 * odd: an unparseable `now` or `horizonEndsAt` makes the whole domain NaN, and `new Date(NaN)
 * .toISOString()` on the axis raises `RangeError`; a lane with no `blocks` array throws inside the
 * `useMemo`. There is no error boundary in this app, so either one takes down the entire page —
 * and this read now fires on its own, so nobody had to ask for it first.
 *
 * Everything NOT checked degrades safely and is deliberately left alone: an unknown block type
 * misses the lookup maps, a bad block timestamp draws a NaN width. Ugly, visibly wrong, and still
 * a page an operator can use.
 */
export function drawable(v: CoverageTimelineView | undefined | null): v is CoverageTimelineView {
  return !!v
    && Array.isArray(v.lanes)
    && v.lanes.every((l) => Array.isArray(l?.blocks))
    && Number.isFinite(Date.parse(v.now))
    && Number.isFinite(Date.parse(v.horizonEndsAt));
}

/**
 * WHEN THE STATION IS GIVEN UP, and when it is taken back — the two ends of the uncovered stretch
 * on the chart, as drawn.
 *
 * HOW LONG the gap lasts is deliberately NOT computed here: mid-cycle these blocks carry only the
 * legs that remain, and a duration measured off them shrinks as the cycle runs. The whole-cycle
 * cost comes from the server (`selfSwapGapSeconds`); this only says where on the clock the
 * current one falls, so a sentence can be phrased in the right tense.
 *
 * `leavesAt` is null once the aircraft has already gone — the first drawn block is then the leg it
 * is flying now, not a departure still ahead.
 */
function selfSwapGap(view: CoverageTimelineView): { leavesAt: string | null; backAt: string } | null {
  const lane = view.lanes.find((l) => l.blocks.some((b) => b.source === 'forecast'));
  if (!lane) return null;
  const forecast = lane.blocks.filter((b) => b.source === 'forecast');
  const away = forecast.find((b) => b.type !== 'on_station');
  if (!away) return null;
  const awayAt = new Date(away.startsAt).getTime();
  const returns = forecast.find((b) => b.type === 'on_station' && new Date(b.startsAt).getTime() > awayAt);
  if (!returns) return null;
  // A departure drawn at `now` is one already under way — the server anchors a leg in progress
  // there rather than at the moment it began.
  const stillAhead = awayAt > new Date(view.now).getTime();
  return { leavesAt: stillAhead ? away.startsAt : null, backAt: returns.startsAt };
}

/** The line an operator gets even without seeing the chart — mirrors "Achieved coverage" below it. */
function summarySentence(view: CoverageTimelineView, timezone: string): string {
  const laneCount = view.lanes.filter((l) => l.isActive).length;
  if (!view.forecastAvailable) {
    // ONE SENTENCE FOR BOTH SHAPES AGAIN. A rotation of one used to need its own, because it HAS
    // a standing forecast and this chart could not draw it — saying "no standing forecast" was
    // false in the direction that tells an operator their unattended rotation has no plan. Its
    // cycle is drawn now, so reaching here means the same thing it means for a handover: nothing
    // is standing to project from.
    return `Rotation timeline loaded: ${laneCount} active drone${laneCount === 1 ? '' : 's'}, `
      + 'no standing forecast to project.';
  }
  // A FORECAST THAT IS STANDING AND DRAWS NOTHING IS ITS OWN SENTENCE (#704).
  //
  // It is not the branch above — there IS a plan, it is mid-execution, and #592's D3 is explicit
  // that calling it "no standing forecast" reads as a rotation with nothing planned. But the
  // chart past NOW is empty, and silence there is the worse reading of the two: the incumbent's
  // bar stops at the NOW rule and the aircraft flying home is drawn STANDBY, which together say
  // the rotation is winding down when it is mid-cycle.
  if (view.forecastDrawn === false) {
    return `Rotation timeline loaded: ${laneCount} active drone${laneCount === 1 ? '' : 's'}. `
      + 'The standing forecast has nothing left to draw — the next one is planned when this '
      + 'cycle finishes.';
  }
  // A SELF-SWAP CYCLE'S FIRST TRANSIT IS THE AIRCRAFT LEAVING, NOT ARRIVING (#564), so the
  // handover sentence below — "next relief at HH:MM" — is not merely unhelpful here, it is
  // inverted: it would announce the moment the station is GIVEN UP as the moment it is renewed.
  // This branch names the gap instead, which is the only thing this shape is read for. The
  // product does not call this a relief anywhere else either; the panel's own control for it is
  // "Approve recall".
  if (view.selfSwapCycle === true) {
    const gap = selfSwapGap(view);
    const cost = view.selfSwapGapSeconds != null
      ? ` The station is uncovered for ${formatDuration(view.selfSwapGapSeconds)} each cycle.`
      : '';
    if (gap === null) {
      return 'Rotation timeline loaded: one aircraft, relieving itself at its own dock.' + cost;
    }
    const stamp = (iso: string): string => `${zonedHHMM(iso, timezone)} `
      + (isUtc(timezone) ? 'UTC' : zoneAbbreviation(iso, timezone));
    // TWO TENSES, because "uncovered from 09:30" is a warning before the aircraft leaves and a
    // stale reading after it has. Once it is away, the useful clock is when it comes back.
    return gap.leavesAt !== null
      ? `Rotation timeline loaded: one aircraft, relieving itself. It gives up the station at ${stamp(gap.leavesAt)} `
        + `and is back on it at ${stamp(gap.backAt)}.${cost}`
      : `Rotation timeline loaded: one aircraft, relieving itself. The station is uncovered now, `
        + `and it is back on station at ${stamp(gap.backAt)}.${cost}`;
  }
  const nextTransit = view.lanes
    .flatMap((l) => l.blocks.map((b) => ({ ...b, callsign: l.callsign })))
    .filter((b) => b.type === 'transit' && b.source === 'forecast')
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())[0];
  // "UTC" for a UTC-default org — byte-identical to the sentence before this feature existed
  // (dateFormat.ts's byte-identical-by-default rule) — or the org's real zone name otherwise.
  const next = nextTransit
    ? `, next relief ${nextTransit.callsign ?? 'unknown'} at ${zonedHHMM(nextTransit.startsAt, timezone)} `
      + (isUtc(timezone) ? 'UTC' : zoneAbbreviation(nextTransit.startsAt, timezone))
    : '';
  return `Rotation timeline loaded: ${laneCount} active drone${laneCount === 1 ? '' : 's'}${next}.`;
}

/**
 * THE LEGEND — the one place every colour and the actual/forecast distinction is readable
 * regardless of how narrow any single bar ends up (review #2/#3).
 *
 * A COMPONENT so the fullscreen view gets the same one. It did not, at first: the operator who
 * expands the chart precisely to read it carefully was the one losing the decoder, and CHARGING
 * and SWAP share a hue — they are separated by a hatch and an in-bar label, and a short swap on a
 * multi-hour horizon carries no label at any width.
 */
/**
 * THE CHART'S TIME AXIS, from a view.
 *
 * Lifted out of the live component's `useMemo` so the attach-time preview draws on exactly the
 * same axis rules (PR 3, S3). A second implementation of "where does this chart start and end"
 * is how the simulator and the status board would come to disagree about a rotation neither of
 * them got wrong.
 *
 * Returns `null` for a view it cannot lay out, which every caller renders as no chart rather than
 * throwing - there is no error boundary in this app.
 */
export function timelineDomain(
  view: CoverageTimelineView | null,
): { start: number; end: number; spanMs: number } | null {
  if (!view) return null;
  const now = new Date(view.now).getTime();
  const horizon = new Date(view.horizonEndsAt).getTime();
  const starts = view.lanes.flatMap((l) => l.blocks.map((b) => new Date(b.startsAt).getTime()));
  const earliestBlock = starts.length > 0 ? Math.min(...starts, now) : now;
  // Bounded lookback (product review #6): a long-lived mission's oldest fetched row must not
  // squeeze "now plus the forecast" — the operationally relevant part — into a sliver.
  const earliest = Math.max(earliestBlock, now - LOOKBACK_MS);
  // At least a 15-minute window so a mission with nothing yet planned still draws a ruler.
  const end = Math.max(horizon, now + 15 * 60_000);
  // Belt and braces behind `drawable`: a domain that is not finite cannot be drawn, and every
  // consumer of it below would rather render nothing than throw.
  if (!Number.isFinite(earliest) || !Number.isFinite(end)) return null;
  return { start: earliest, end, spanMs: Math.max(1, end - earliest) };
}

export function TimelineLegend({ showDecision = false }: { showDecision?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
      {LEGEND_ORDER.map((type) => (
        <span key={type} className="inline-flex items-center gap-1 uppercase tracking-wider">
          <span
            aria-hidden="true"
            className={cn('inline-block size-2.5 rounded-sm border', BLOCK_CLASS[type])}
            style={type === 'swap' ? SWAP_HATCH_STYLE : undefined}
          />
          {BLOCK_LABEL[type]}
        </span>
      ))}
      <span className="inline-flex items-center gap-1 uppercase tracking-wider">
        <span aria-hidden="true" className="inline-block size-2.5 rounded-sm border border-solid border-foreground/50 bg-foreground/10" />
        Actual
      </span>
      <span className="inline-flex items-center gap-1 uppercase tracking-wider">
        <span aria-hidden="true" className="inline-block size-2.5 rounded-sm border border-dashed border-foreground/50 bg-foreground/10" />
        Forecast
      </span>
      {/* THE DECISION RING NEEDS DECODING TOO. This file's own rule (see the header) is that a
          narrow bar cannot rely on colour or a border alone, so every cue gets a
          width-independent partner — and the amber ring shipped without one. It lands on a
          `transit` block, which is the one type never wide enough to carry its own label inline,
          on a chart where `charging` and `swap` are already amber-FILLED. The legend is what tells
          an operator that a ring is not a fill. Product review, 2026-09-07.

          Offered only where a decision can exist: the attach wizard's preview draws a projection
          with no rotation action behind it, so a key for a mark it never renders would be
          decoding nothing. */}
      {showDecision && (
        <span className="inline-flex items-center gap-1 uppercase tracking-wider">
          <span aria-hidden="true" className="inline-block size-2.5 rounded-sm ring-1 ring-status-amber-marker bg-foreground/10" />
          Decision pending
        </span>
      )}
    </div>
  );
}

/**
 * HOW OLD THE PICTURE IS, AND THE WAY BACK FROM A BAD READ — rendered wherever the chart is.
 *
 * These four (the read stamp, the stale badge, the error and Refresh) lived only in the inline
 * header, which made the fullscreen view the less trustworthy of the two: a plan frozen at the
 * last good server `now`, no sign that later reads were failing, and no control to retry.
 */
function TimelineFreshness({ readAt, stale, error, busy, timezone, onRefresh, describedById }: {
  readAt: number | null;
  stale: boolean;
  error: string | null;
  busy: boolean;
  timezone: string;
  onRefresh: () => void;
  describedById: string;
}) {
  return (
    <>
      {readAt !== null && (
        <span className="text-[10px] font-mono tabular-nums text-muted-foreground">
          read {zonedHHMM(new Date(readAt).toISOString(), timezone)}
          {isUtc(timezone) ? ' UTC' : ` ${zoneAbbreviation(readAt, timezone)}`}
        </span>
      )}
      {stale && (
        <span className="text-[10px] uppercase tracking-wider text-status-amber-fg">
          Stale · last read failed
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        aria-describedby={describedById}
        aria-disabled={busy}
        aria-busy={busy}
        onClick={() => { if (!busy) onRefresh(); }}
      >
        {busy ? 'Computing…' : 'Refresh'}
      </Button>
      {error !== null && (
        <p role="alert" className="flex w-full items-start gap-1.5 text-xs text-status-amber-fg">
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
          {error}
        </p>
      )}
    </>
  );
}

/** The lane and block a `selectedKey` currently points at, resolved out of the view. */
export interface BlockSelection {
  key: string;
  lane: TimelineLane;
  block: TimelineBlock;
}

/**
 * RESOLVED FROM THE VIEW EVERY RENDER, never held as state beside it.
 *
 * The chart re-reads itself whenever the rotation moves, and a selected bar can disappear in one
 * of those: a forecast is superseded, a cycle completes, an aircraft is stood down. Keeping the
 * block itself in state means rendering a detail panel describing a bar that is no longer on the
 * chart. Keeping only the KEY and looking it up means the selection heals itself — the bar goes,
 * the detail goes with it, and no effect has to notice.
 */
function findSelection(view: CoverageTimelineView, key: string | null): BlockSelection | null {
  if (key === null) return null;
  for (const lane of view.lanes) {
    for (const block of lane.blocks) {
      if (blockKey(lane, block) === key) return { key, lane, block };
    }
  }
  return null;
}

/**
 * WHAT THE SELECTED BAR IS - read-only, and always occupying its own height.
 *
 * THE HEIGHT IS RESERVED, which is the whole reason this is a strip and not a popover. The chart
 * is the last element in the timeline block, and what sits under it is the battery-policy line and
 * the lifecycle drawer's toggle, inside a `max-h-[45vh]` scroller. A detail region that appears on
 * the first click moves that toggle out from under the operator's cursor - the same class of
 * hazard the action card's placement comment records a near-miss from. Empty, it carries the
 * hint instead, and nothing below it ever moves.
 *
 * NO CONTROLS LIVE HERE (D1). The decision bar sends focus to the action card the panel already
 * renders; this strip never grows an Approve of its own.
 */
function TimelineBlockDetail({ selection, timezone, expanded, decisionActionable = false }: {
  selection: BlockSelection | null;
  timezone: string;
  expanded: boolean;
  /** Same flag the bars are drawn by, so the chip and the ring cannot disagree. */
  decisionActionable?: boolean;
}) {
  const cls = cn(
    'rounded border border-border/70 px-2 py-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5',
    expanded ? 'min-h-11 text-xs' : 'min-h-9 text-[11px]',
  );

  if (selection === null) {
    return (
      <div className={cls}>
        <span className="text-muted-foreground">Select a bar for detail.</span>
      </div>
    );
  }

  const { lane, block } = selection;
  const seconds = blockSeconds(block);

  return (
    // `aria-live` is deliberately absent. The bar's own accessible name carries every fact in this
    // strip — including the duration, which is why `blockAriaLabel` appends it — so a screen
    // reader announces it on activation; a live region would say the same sentence a second time.
    <div className={cls} data-testid="timeline-block-detail">
      <span className="uppercase tracking-wider font-semibold">{lane.callsign ?? 'UNKNOWN'}</span>
      <span className="uppercase tracking-wider text-muted-foreground">{BLOCK_LABEL[block.type]}</span>
      <span className="uppercase tracking-wider text-muted-foreground">
        {block.source === 'actual' ? 'Actual' : 'Forecast'}
      </span>
      {/* THE ZONE IS NAMED. `zonedHHMM` drops it on purpose for a dense repeated axis label and
          for a per-block accessible name; this is neither — it is a single standalone stamp pair,
          and every other standalone stamp in this module says which clock it is on. */}
      <span className="font-mono tabular-nums" data-numeric>
        {zonedHHMM(block.startsAt, timezone)}
        {' - '}
        {block.endsAt === null ? 'open' : zonedHHMM(block.endsAt, timezone)}
        {` ${isUtc(timezone) ? 'UTC' : zoneAbbreviation(block.startsAt, timezone)}`}
      </span>
      {seconds !== null && (
        <span className="font-mono tabular-nums text-muted-foreground" data-numeric>
          {formatDuration(seconds)}
        </span>
      )}
      {/* ONE WORD, NOT A SENTENCE. "Decision pending - see the panel above." was ~230 px of 11 px
          text, which at the ~440 px inline width wrapped this strip to a second line EVERY time
          the decision bar was selected — moving the battery-policy line and the lifecycle
          drawer's toggle under it, which is the exact hazard the reserved height exists to
          prevent. The instruction it carried is already in the bar's own accessible name.
          Product review, 2026-09-07. */}
      {block.actionId !== undefined && decisionActionable && (
        <span className="uppercase tracking-wider text-status-amber-fg">Decision</span>
      )}
    </div>
  );
}

/**
 * THE CHART ITSELF, drawn once and rendered at two very different widths: roughly 440 px inline
 * in the panel, and the whole viewport expanded.
 *
 * Extracted so those two are not two implementations that drift. Everything that has to answer to
 * width — the ruler's step, the lane height, how wide the label column is, whether a bar can carry
 * its own text — is derived from the MEASURED track rather than tuned once for the inline case.
 */
export function TimelineChart({
  view, domain, timezone, expanded = false, selectedKey = null, onSelect,
  decisionActionable = false,
}: {
  view: CoverageTimelineView;
  domain: { start: number; end: number; spanMs: number };
  timezone: string;
  expanded?: boolean;
  /**
   * SELECTION IS OPT-IN, and the absence of `onSelect` is what turns it off — not a boolean
   * beside it that could disagree with it. Without a handler every bar renders exactly as it did
   * before PR 4: a focusable `div` with its accessible name and nothing to press.
   *
   * The attach wizard's preview (`CoverageTimelinePreview`) is the caller that wants that. It
   * draws a PROJECTION, so there is no coverage row behind it, no action card to move focus to and
   * no detail strip to answer a click with. Interactive bars there would be affordances pointing
   * at nothing.
   */
  selectedKey?: string | null;
  onSelect?: (block: TimelineBlock, key: string) => void;
  /**
   * Is there actually a card for a decision bar to reach right now?
   *
   * ONE SOURCE OF TRUTH FOR "THIS BAR DECIDES". An `actionId` alone is not enough: the chart and
   * the action card are fed by different reads, so the bar can outlive the card. When that
   * happens this goes false and the bar stops CLAIMING anything - no amber ring, no "activate to
   * review it", no Decision chip in the strip - rather than making a promise and then refusing it.
   * Two statements about one launch disagreeing on one screen is the failure this module's own
   * comments name. Engineering review, 2026-09-07.
   */
  decisionActionable?: boolean;
}) {
  const [trackRef, trackPx] = useTrackWidth(MIN_TRACK_PX);
  /**
   * NEVER SCROLL FURTHER THAN THE VIEWPORT EARNED. A flat 720 px floor when expanded is an
   * upgrade on a monitor and a downgrade on a phone, where 96vw minus the label column leaves
   * less visible track than the inline chart had while making the thing you scroll longer. The
   * expanded floor is therefore what the dialog actually has room for, bounded by the inline one.
   */
  const minTrackPx = expanded
    ? Math.max(MIN_TRACK_PX, Math.min(MIN_TRACK_EXPANDED_PX, trackPx))
    : MIN_TRACK_PX;
  const drawnPx = Math.max(trackPx, minTrackPx);

  const nowMs = new Date(view.now).getTime();
  const pct = (ms: number): number => ((ms - domain.start) / domain.spanMs) * 100;
  const nowPct = pct(nowMs);

  const step = tickStepMs(domain.spanMs, drawnPx);
  const ticks = axisTicks(domain.start, domain.end, step);
  /**
   * Suppressed where it would collide with the NOW label, which outranks any tick.
   *
   * BOXES, NOT CENTRES. Both labels flip to edge alignment near the window bounds, and a
   * centre-to-centre test does not know that — at the left edge, where a freshly attached rotation
   * puts NOW by construction, a tick 50 px away passes the test and still overlaps by 19. So each
   * label's box is derived from its own alignment and the two are tested for overlap.
   */
  const labelBox = (posPct: number, widthPx: number, edgePct: number): [number, number] => {
    const centre = (posPct / 100) * drawnPx;
    if (posPct < edgePct) return [centre, centre + widthPx];
    if (posPct > 100 - edgePct) return [centre - widthPx, centre];
    return [centre - widthPx / 2, centre + widthPx / 2];
  };
  const nowBox = labelBox(nowPct, NOW_LABEL_PX, 6);
  const collidesWithNow = (tickMs: number): boolean => {
    if (nowPct < 0 || nowPct > 100) return false;
    const [tickFrom, tickTo] = labelBox(pct(tickMs), TICK_LABEL_PX, 4);
    return tickFrom < nowBox[1] + TICK_LABEL_GUTTER_PX && tickTo + TICK_LABEL_GUTTER_PX > nowBox[0];
  };

  return (
    <div className="flex gap-2">
      {/* Row labels are OUTSIDE the scroll region and stay put while the track scrolls
          (review #5) — a real Gantt convention, and without it, scrolling right to see a
          later block scrolls away the one thing that says whose row it is. */}
      <div className={cn('shrink-0', expanded ? 'w-28 sm:w-40' : 'w-28')}>
        {view.lanes.map((lane) => (
          <div
            key={lane.droneId}
            className={cn(
              'py-1 flex flex-col justify-center border-b border-border/50 last:border-0',
              expanded ? 'min-h-14' : 'min-h-10',
            )}
          >
            <div className={cn(
              'uppercase tracking-wider truncate',
              expanded ? 'text-xs' : 'text-[11px]',
              lane.isActive ? 'text-foreground' : 'text-muted-foreground',
            )}
            >
              {lane.callsign ?? 'UNKNOWN'}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              #{lane.rotationOrder}{!lane.isActive && ' · stood down'}
              {expanded && lane.dockName ? ` · ${lane.dockName}` : ''}
            </div>
          </div>
        ))}
      </div>

      {/* `tabIndex=0` + a label make this scrollable by keyboard alone (arrow keys, once
          focused) — a mouse or touchscreen was otherwise the only way to see past ~560px
          of a window that can span 4+ hours (review #5, WCAG 2.1.1). */}
      <div
        ref={trackRef}
        className="overflow-x-auto flex-1"
        tabIndex={0}
        role="group"
        aria-label="Rotation timeline chart, scrollable"
      >
        <div className="relative" style={{ minWidth: `${minTrackPx}px` }}>
          {view.lanes.map((lane) => (
            <div
              key={lane.droneId}
              className={cn(
                'py-1 flex items-center border-b border-border/50 last:border-0',
                expanded ? 'min-h-14' : 'min-h-10',
              )}
            >
              {/* h-8 INLINE, NOT h-6, AND THE REASON IS THE BUTTONS (PR 4). A bar is
                  `top-0.5 bottom-0.5` inside this track, so h-6 made every one of them a 20 px
                  target — under WCAG 2.2 SC 2.5.8's 24x24 floor, which this module already holds
                  itself to by name in `CoverageDrawer.tsx`. Inert `div`s were out of scope for it;
                  buttons are not. h-8 puts the bar at 28 px and still fits the lane's `min-h-10`,
                  so no row changes height and the label column stays aligned.

                  THE WIDTH FLOOR IS NOT FIXED and is recorded as such — a four-minute transit on a
                  six-hour domain is a few pixels wide, and widening the hit area past the drawn
                  bar would overlap the neighbour it abuts, since the server emits contiguous
                  blocks. See the record's "What is NOT verified". */}
              <div className={cn(
                'relative w-full rounded bg-muted/30 overflow-hidden',
                expanded ? 'h-10' : 'h-8',
              )}
              >
                {/* THE RULER, inside each lane's own track and UNDER its bars — the conventional
                    Gantt layering. Drawn over the top instead, it either streaks across every
                    coloured block or has to be faded to the point of invisibility (a 7% line on
                    this track computes to about 1.2:1, which is not a gridline, it is a rumour). */}
                {ticks.map((t) => (
                  <div
                    key={t}
                    aria-hidden="true"
                    className="absolute top-0 bottom-0 w-px bg-foreground/25"
                    style={{ left: `${pct(t)}%` }}
                  />
                ))}

                {/* The forecast wash — everything at/after NOW, as a REGION, so
                    actual-vs-forecast survives even where every block in it is too
                    narrow to show its own border style (review #3). */}
                {nowPct < 100 && (
                  <div
                    className="absolute top-0 bottom-0 right-0 bg-foreground/[0.04]"
                    style={{ left: `${Math.max(0, nowPct)}%` }}
                    aria-hidden="true"
                  />
                )}
                {nowPct >= 0 && nowPct <= 100 && (
                  <div
                    className="absolute top-0 bottom-0 w-px bg-foreground/70 z-10"
                    style={{ left: `${nowPct}%` }}
                    aria-hidden="true"
                  />
                )}
                {lane.blocks.map((block, i) => {
                  const startMs = new Date(block.startsAt).getTime();
                  const endMs = resolveBlockEnd(block, lane.blocks, nowMs, domain.end);
                  const leftPct = pct(startMs);
                  const rightPct = pct(endMs);
                  // CLIPPED AT BOTH ENDS, not just the left. Clamping the left edge to 0 without
                  // taking the same amount off the width slides the bar's right edge into time
                  // it did not happen in: a segment that ran 07:00-07:40, on a window starting at
                  // 10:00, drew as ON STATION from 10:00 to 10:40 while its own accessible name
                  // said 07:00. Any rotation older than the two-hour lookback has several of
                  // those stacked at the left edge, and the ruler this slice adds is what lets an
                  // operator read the false claim off the chart.
                  if (rightPct <= 0 || leftPct >= 100) return null;
                  const clampedLeft = Math.max(0, leftPct);
                  const clampedWidth = Math.max(0.5, Math.min(rightPct, 100) - clampedLeft);

                  const key = blockKey(lane, block);
                  // Stability for the selection, uniqueness for React — see `blockKey`.
                  const reactKey = `${key}:${i}`;
                  const selected = selectedKey === key;
                  /**
                   * THE ONE BAR THAT REACHES A CONTROL. Every other bar answers a click with the
                   * read-only detail strip; this one additionally sends the operator to the
                   * decision that is already on the panel above (D1 — the chart never grows its
                   * own Approve).
                   */
                  const decides = block.actionId !== undefined && decisionActionable;
                  const name = blockAriaLabel(block, lane.callsign, timezone)
                    + (onSelect === undefined
                      ? ''
                      : decides
                        // SAY WHERE IT GOES. Activating this bar moves focus off the chart, and
                        // from the fullscreen dialog it also closes the dialog (D4). A control
                        // that relocates a keyboard operator without warning them first is the
                        // thing the panel's own focus rules exist to prevent.
                        ? ' — decision pending, activate to review it'
                        : ', activate for detail');
                  const label = (clampedWidth / 100) * drawnPx >= labelFitsPx(block.type, expanded)
                    // IN PIXELS, NOT IN PERCENT. The same 3% block is 13 px inline and 40 px
                    // expanded; a fixed percentage threshold either hides labels there is room
                    // for or prints clipped ones where there is not.
                    ? BLOCK_LABEL[block.type]
                    : null;
                  const className = cn(
                    'absolute top-0.5 bottom-0.5 rounded-sm border uppercase tracking-wider overflow-hidden whitespace-nowrap px-1 flex items-center',
                    // The one thing read OFF the chart. Leaving it at 9 px in a 40 px bar is
                    // what makes an expanded view "the same picture, bigger".
                    expanded ? 'text-[11px]' : 'text-[9px]',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foreground',
                    BLOCK_CLASS[block.type],
                    block.source === 'forecast' && 'border-dashed',
                    // PRESSABLE, AND SAYING SO BEFORE THE PRESS. Tailwind v4's preflight drops the
                    // UA `cursor: pointer` on `<button>` and this repo has no global rule putting
                    // it back — only `ui/button.tsx` does — so a hand-rolled button is an arrow
                    // cursor over a chart with no hover state at all. On the button branch only,
                    // so the preview's inert bars are untouched.
                    onSelect !== undefined && 'cursor-pointer hover:brightness-125',
                    // THE THREE RING CASES, WRITTEN OUT, because `cn` is `twMerge` and it resolves
                    // `ring-*` as ONE group: `ring-2 ring-foreground` listed after
                    // `ring-1 ring-status-amber-marker` deletes the amber outright. Selecting the
                    // decision bar therefore erased the only mark that said a decision was
                    // pending, permanently, since the selection persists. Product review,
                    // 2026-09-07.
                    //
                    // AMBER MATCHES THE CARD IT POINTS AT — that card is amber for exactly as long
                    // as something is outstanding, so the two agree by construction rather than by
                    // a second rule about when to draw this. Selection thickens it rather than
                    // replacing it.
                    decides && onSelect !== undefined && !selected && 'ring-1 ring-status-amber-marker',
                    decides && onSelect !== undefined && selected && 'ring-2 ring-status-amber-marker',
                    // SELECTION IS A RING, NOT A FILL. The fill is the block TYPE and is the whole
                    // legend; recolouring it to say "selected" would make a bar lie about what it
                    // is. `ring-offset` would be eaten by the lane's `overflow-hidden`.
                    selected && !(decides && onSelect !== undefined) && 'ring-2 ring-foreground',
                  );
                  const style: CSSProperties = {
                    left: `${clampedLeft}%`,
                    width: `${clampedWidth}%`,
                    ...(block.type === 'swap' ? SWAP_HATCH_STYLE : {}),
                  };

                  // TWO ELEMENTS, WRITTEN OUT. A `div` that grows an `onClick` is not a button to
                  // a screen reader or to the keyboard, and the alternative — one tag computed
                  // into a variable — hides which props belong to which element from the reader
                  // and from the type checker.
                  if (onSelect === undefined) {
                    return (
                      <div
                        key={reactKey}
                        tabIndex={0}
                        aria-label={name}
                        title={name}
                        className={className}
                        style={style}
                      >
                        {label}
                      </div>
                    );
                  }
                  return (
                    <button
                      key={reactKey}
                      type="button"
                      aria-label={name}
                      // `aria-current`, NOT `aria-pressed`. These are not toggles: a second press
                      // re-selects rather than clearing, and on the decision bar the press is a
                      // navigation. A screen reader announcing "pressed" on a control that cannot
                      // be un-pressed describes a widget this is not.
                      aria-current={selected ? true : undefined}
                      title={name}
                      className={className}
                      style={style}
                      onClick={() => onSelect(block, key)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

        </div>

        {/* A REAL TIME AXIS. It was three labels — window start, NOW, window end — which is a
            caption, not a ruler: nothing on the chart could be read off against a clock. */}
        <div
          className="relative h-4 mt-1 text-[10px] font-mono tabular-nums text-muted-foreground"
          style={{ minWidth: `${minTrackPx}px` }}
        >
          {ticks.map((t) => (collidesWithNow(t) ? null : (
            <span
              key={t}
              className={cn(
                'absolute whitespace-nowrap',
                pct(t) < 4 ? '' : pct(t) > 96 ? '-translate-x-full' : '-translate-x-1/2',
              )}
              style={{ left: `${pct(t)}%` }}
            >
              {zonedHHMM(new Date(t).toISOString(), timezone)}
            </span>
          )))}
          {nowPct >= 0 && nowPct <= 100 && (
            <span
              className={cn(
                'absolute whitespace-nowrap text-foreground',
                nowPct < 6 ? '' : nowPct > 94 ? '-translate-x-full' : '-translate-x-1/2',
              )}
              style={{ left: `${nowPct}%` }}
            >
              NOW {zonedHHMM(view.now, timezone)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function MissionCoverageTimeline({
  missionId, revision = 0, onActOnDecision, decisionReachable = false,
}: {
  missionId: string;
  /**
   * Called when the operator activates the one bar that stands for the standing rotation action
   * (`block.actionId`). The panel owns the action card, so it owns what "act" means: pin the
   * inputs open, bring the card into view, put focus on it (D1).
   *
   * Absent, the chart is still fully selectable and every bar still answers with detail — the
   * decision bar simply has nothing extra to reach. That is what keeps this prop from becoming a
   * second switch that can disagree with whether a card is rendered at all.
   */
  onActOnDecision?: () => void;
  /**
   * Is that card actually on screen right now?
   *
   * THE CHART AND THE CARD ARE FED BY DIFFERENT READS. This one re-reads `/timeline` on its own
   * `REFRESH_FLOOR_MS` cadence; the card comes from the panel's socket/poll arbitration. Around a
   * suspend, or a replacement that has just completed, the bar outlives the card by up to a read —
   * and permanently if `suspendRotation`'s forecast cleanup failed, which it is allowed to do
   * ("a stale forecast is a display problem rather than a safety one").
   *
   * Without this the decision bar's press is a no-op that also closes the dialog: the operator
   * loses the chart they were reading and gets no explanation. Product review, 2026-09-07.
   */
  decisionReachable?: boolean;
  /**
   * Bumped by the panel on every coverage or rotation frame for this mission — the plan has
   * moved, re-read it.
   *
   * A COUNTER RATHER THAN THIS COMPONENT OPENING ITS OWN SOCKET. `useCoverageEvents` exists
   * because the mission page had no socket; opening a second one here would double the
   * connections per open panel to carry frames the panel is already receiving. The panel owns
   * the subscription, this owns the chart.
   */
  revision?: number;
}) {
  const timezone = useOrgTimezone();
  const uid = useId();
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<CoverageTimelineView | null>(null);
  /** A read failed but the last good plan is still on screen — marked, never erased. */
  const [stale, setStale] = useState(false);
  /** When the plan on screen was read. A Gantt whose NOW does not move must say how old it is. */
  const [readAt, setReadAt] = useState<number | null>(null);
  /**
   * THE KEY ONLY, never the block — see `findSelection`. Shared by the inline chart and the
   * fullscreen one on purpose: expanding the chart to look harder at the bar you just selected and
   * finding nothing selected is a state reset the operator did not ask for.
   */
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /**
   * Set when a decision bar is activated from INSIDE the dialog, and read once by
   * `onCloseAutoFocus` (D4). A ref rather than state: it must be readable in the close handler
   * that radix fires during the same interaction, and it must not cause a render of its own.
   */
  const actOnDialogClose = useRef(false);
  /** An OPERATOR-INITIATED read is running. Background reads deliberately do not set this. */
  const [busy, setBusy] = useState(false);
  /** What to say to a screen reader about an action the operator took, as opposed to the plan. */
  const [ack, setAck] = useState('');
  const [fullscreen, setFullscreen] = useState(false);

  /** Guards against a burst of frames stacking overlapping reads on a moderately expensive query. */
  const inFlight = useRef(false);
  const pending = useRef(false);
  /** Has a read ever succeeded? Read from a ref so a running load never judges it on a stale closure. */
  const hasView = useRef(false);
  const lastLoadedAt = useRef(0);
  const loadRef = useRef<((userInitiated?: boolean) => Promise<void>) | null>(null);
  // A response for the mission the operator has already navigated away from must never paint.
  const currentMission = useRef(missionId);
  useEffect(() => { currentMission.current = missionId; }, [missionId]);

  const load = useCallback(async (userInitiated = false) => {
    // NOT DROPPED, DEFERRED. A frame arriving while a read is open is a real change, and the
    // effect that scheduled it has already spent its timer — returning here without recording it
    // would leave the chart holding a plan it has been told is out of date.
    if (inFlight.current) {
      pending.current = true;
      return;
    }
    inFlight.current = true;
    if (userInitiated) {
      setBusy(true);
      setAck('Refreshing the rotation timeline');
    }
    /** Keeps a plan on screen when a read fails, the way the forecast row above this one does. */
    const fail = (message: string): void => {
      setError(message);
      // Nothing to keep on a first read, so the error IS the whole state. After that, the plan
      // stays on screen and is MARKED — erasing a chart an operator is mid-read of says something
      // quite different from "we could not ask again".
      setStale(hasView.current);
    };
    try {
      const data = await api.get<{ data: CoverageTimelineView }>(
        `/api/coverage/${missionId}/timeline`,
        { signal: AbortSignal.timeout(TIMELINE_REQUEST_TIMEOUT_MS) },
      );
      if (currentMission.current !== missionId) return;
      // CHECKED AT THE BOUNDARY, because this read is no longer something an operator asked for
      // — it fires whenever the coverage panel is opened. A body without lanes (an older API
      // mid-rolling-deploy, a proxy's error page) used to be impossible to reach without a
      // deliberate click; unchecked it now throws inside render and takes the whole mission
      // detail page down with it. The panel makes the same defence for its own frames.
      if (!drawable(data?.data)) {
        // Said in its own words rather than folded into the catch below, whose sentence is about
        // a read that FAILED — this one succeeded and answered with something else.
        fail('The rotation timeline came back in a shape this page cannot draw');
        return;
      }
      setView(data.data);
      hasView.current = true;
      setError(null);
      setStale(false);
      setReadAt(Date.now());
    } catch (err) {
      if (currentMission.current !== missionId) return;
      fail(getApiErrorMessage(err, 'The rotation timeline could not be computed'));
    } finally {
      inFlight.current = false;
      lastLoadedAt.current = Date.now();
      if (userInitiated) {
        setBusy(false);
        // Distinct every time (it carries the second), so a refresh that changed nothing is still
        // acknowledged — pressing a button and hearing silence is the failure this avoids.
        setAck(`Rotation timeline refreshed at ${new Date().toLocaleTimeString('en-US', { timeZone: timezone, hourCycle: 'h23' })}`);
      }
      if (pending.current) {
        pending.current = false;
        void loadRef.current?.(false);
      }
    }
  }, [missionId, timezone]);

  useEffect(() => { loadRef.current = load; }, [load]);

  /**
   * THE PLAN IS THE INTERFACE, so it loads with the panel body rather than behind a button.
   *
   * This is not a read on every mission-detail visit: the coverage panel is collapsed by default
   * and this component only mounts inside its expanded body, so the read happens when an operator
   * opens continuous coverage — which is the moment they are asking this exact question. Before
   * this, the schedule was two clicks away and both of them came after the rotation was already
   * committed.
   */
  useEffect(() => {
    setView(null);
    hasView.current = false;
    setError(null);
    setStale(false);
    setReadAt(null);
    void loadRef.current?.(false);
  }, [missionId]);

  /**
   * A FLOOR, NOT A DEBOUNCE. Frames arrive in bursts — a phase moving publishes several inside a
   * few seconds — and each one is a real change worth re-reading for. Waiting out a quiet period
   * would delay the chart exactly while the rotation is busiest, so instead the first frame after
   * the floor re-reads immediately and the rest of the burst is coalesced into one trailing read.
   */
  useEffect(() => {
    if (revision === 0) return undefined;
    const wait = Math.max(0, REFRESH_FLOOR_MS - (Date.now() - lastLoadedAt.current));
    const id = setTimeout(() => { void loadRef.current?.(false); }, wait);
    return () => clearTimeout(id);
  }, [revision]);

  /**
   * A CEILING AS WELL AS A FLOOR, because every NOW on this chart is the SERVER'S `now` — the NOW
   * rule, the forecast wash boundary, the right edge of every open bar, and the axis label itself.
   * None of it advances on its own.
   *
   * Frames are published on CHANGE, so a rotation sitting quietly in `covered` sends nothing, and
   * without this the chart would keep printing `NOW 14:20` at 14:32 with a forecast transit drawn
   * "ahead" that is already happening — while the chip a few pixels above it, arbitrated against
   * the panel's own 15 s poll, says the replacement is due now. Two statements about one launch,
   * on one screen, disagreeing.
   */
  useEffect(() => {
    const id = setInterval(() => { void loadRef.current?.(false); }, MAX_STALENESS_MS);
    return () => clearInterval(id);
  }, []);


  const domain = useMemo(() => timelineDomain(view), [view]);

  /**
   * A modal must never be left holding nothing. A background re-read can empty the chart while it
   * is open — the last aircraft leaves the rotation, someone else detaches coverage — and the
   * Expand button that opened it unmounts with the chart, so radix has no element to restore focus
   * to and a keyboard operator lands on the body, inside a scroller, above an armed Detach.
   */
  useEffect(() => {
    if (view === null || domain === null || view.lanes.length === 0) setFullscreen(false);
  }, [view, domain]);

  /**
   * SELECT ALWAYS, ACT ONLY ON THE DECISION BAR.
   *
   * The selection is set first and unconditionally, so the strip answers every bar — including
   * the decision bar, whose detail stays on screen behind the card the operator is being sent to.
   *
   * FROM THE DIALOG, THE CLOSE COMES FIRST. The action card is rendered by the panel and does not
   * exist inside the dialog, so focusing it while radix still has the page `aria-hidden` behind an
   * overlay would put focus somewhere a screen reader cannot follow. The flag is read back in
   * `onCloseAutoFocus`, which is the one moment radix hands focus over.
   */
  const selectBlock = useCallback((block: TimelineBlock, key: string, fromDialog: boolean) => {
    setSelectedKey(key);
    /**
     * THE SAME PREDICATE THE BAR WAS DRAWN BY. With `decisionActionable` false the bar never
     * claimed to decide anything — no ring, no suffix, no chip — so there is nothing to refuse
     * here and nothing to announce: it selects and shows its detail like any other bar.
     *
     * An earlier cut announced a refusal instead, which was wrong twice over: the bar still
     * looked and read like a decision, and the announcement went into a live region that radix
     * `aria-hidden`s whenever the dialog is open, so from fullscreen it was silent anyway.
     */
    if (block.actionId === undefined || onActOnDecision === undefined || !decisionReachable) return;
    if (fromDialog) {
      actOnDialogClose.current = true;
      setFullscreen(false);
      return;
    }
    onActOnDecision();
  }, [onActOnDecision, decisionReachable]);

  const headingId = `${uid}-timeline`;
  const fullscreenTitleId = `${uid}-timeline-fullscreen`;
  const selection = view === null ? null : findSelection(view, selectedKey);
  /**
   * Is a decision bar actually ON this chart, and reachable?
   *
   * BOTH HALVES, because the legend decodes a mark and a key for a mark that is not drawn reads
   * as a rendering fault — the same rule the self-swap caption is gated on. `decisionReachable`
   * alone would decode the ring on every healthy rotation between forecasts.
   */
  const decisionActionable = decisionReachable && view !== null
    && view.lanes.some((lane) => lane.blocks.some((b) => b.actionId !== undefined));

  return (
    <div className="space-y-2 pt-1 border-t border-border">
      <div className="flex flex-wrap items-center gap-2">
        <span id={headingId} className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Rotation timeline
        </span>
        {/* WHEN IT WAS READ, THE WAY BACK FROM A BAD ONE, AND WHETHER THE LAST ONE FAILED.
            Nothing on this chart advances on its own — the NOW rule, the forecast wash and the
            axis are all the SERVER'S now — so a Gantt that looks live and is not is worse than one
            that says how old it is.

            BUSY TRACKS THE OPERATOR'S OWN READ, never a background one: a control whose name and
            disabled state flip under a keyboard user on somebody else's schedule is its own bug,
            and it silently swallowed presses that landed during a refresh. */}
        <TimelineFreshness
          readAt={readAt}
          stale={stale}
          error={null}
          busy={busy}
          timezone={timezone}
          onRefresh={() => void load(true)}
          describedById={headingId}
        />
        {/* EXPAND, NOT A NEW ROUTE. Four lanes over eight hours is not a 440 px picture, and the
            operator reading it is mid-task on this page — sending them somewhere else to see the
            plan is what "Show plan" already cost. Offered only when there is a chart to expand. */}
        {view !== null && domain !== null && view.lanes.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            aria-label="Expand rotation timeline to fullscreen"
            aria-haspopup="dialog"
            onClick={() => setFullscreen(true)}
          >
            <Maximize2 className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
            Expand
          </Button>
        )}
      </div>

      {/* FULLSCREEN IS NOT A THINNER VIEW. Everything the inline chart says about itself — the
          legend that decodes the blocks, how old the read is, whether the last one failed, and the
          control that retries it — is here too, because the operator who expands the chart is the
          one reading it hardest. Radix aria-hides the page behind the modal, so anything left
          outside is gone for a screen reader as well as behind the overlay. */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent
          className="w-[96vw] max-w-[96vw] sm:max-w-[96vw] max-h-[92vh] overflow-auto"
          /* THE ONE HANDOVER RADIX GIVES US (D4). Ordinarily it restores focus to the Expand
             button, which is right for every close except this one: the operator activated a
             decision bar and is being taken to the card behind the dialog. Preventing the default
             and calling the panel's own focus move is the only way those two do not race.

             The flag is cleared unconditionally, so an Escape or an overlay click that follows an
             unrelated selection never inherits it. */
          onCloseAutoFocus={(e) => {
            if (!actOnDialogClose.current) return;
            actOnDialogClose.current = false;
            /* RE-CHECKED HERE, not only at the press. Radix fires this at UNMOUNT — the content
               animates out for ~100 ms and focus-scope dispatches from a `setTimeout(0)` after
               that — so a `not_planned` frame can cancel the action in between. Calling
               `preventDefault` on the way to a card that no longer exists suppresses radix's own
               restore and drops focus on `document.body`, inside the scroller above an armed
               Detach, which is the failure the empty-view effect above was written to prevent.
               Engineering review, 2026-09-07. */
            if (!decisionReachable) return;
            e.preventDefault();
            onActOnDecision?.();
          }}
        >
          <DialogHeader>
            {/* NO `id` OVERRIDE HERE. Radix points the dialog's own `aria-labelledby` at the id it
                generates for this title, so replacing it leaves the dialog with no accessible
                name at all. The Refresh button below gets its description from its own span. */}
            <DialogTitle className="text-sm uppercase tracking-wider">Rotation timeline</DialogTitle>
            {/* A live region, not static text: the plan re-reads itself while this is open, and
                the page's own announcement is aria-hidden behind the overlay. */}
            <DialogDescription className="text-xs" aria-live="polite">
              {view !== null ? summarySentence(view, timezone) : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2">
            <span id={fullscreenTitleId} className="sr-only">Rotation timeline</span>
            <TimelineFreshness
              readAt={readAt}
              stale={stale}
              error={error}
              busy={busy}
              timezone={timezone}
              onRefresh={() => void load(true)}
              describedById={fullscreenTitleId}
            />
          </div>

          {view !== null && view.lanes.length === 0 && (
            <p className="text-[11px] text-muted-foreground">
              No aircraft in this rotation to draw.
            </p>
          )}
          {view !== null && domain !== null && view.lanes.length > 0 && (
            <>
              <TimelineLegend showDecision={decisionActionable} />
              <TimelineChart
                view={view}
                domain={domain}
                timezone={timezone}
                expanded
                selectedKey={selectedKey}
                onSelect={(block, key) => selectBlock(block, key, true)}
                decisionActionable={decisionActionable}
              />
              <TimelineBlockDetail
                selection={selection}
                timezone={timezone}
                expanded
                decisionActionable={decisionActionable}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* TWO REGIONS, TWO SUBJECTS. This one is the PLAN, and it changes only when the plan does —
          the chart now re-reads on its own every time the rotation moves, and blanking this to
          "Computing…" and back on each of those made a screen reader recite the whole summary
          every few seconds, unasked, queued ahead of the panel's own safety announcements. An
          unchanged plan re-read produces identical text, and identical text is not announced. */}
      <p role="status" aria-live="polite" className="sr-only">
        {view !== null ? summarySentence(view, timezone) : ''}
      </p>
      {/* And this one is the OPERATOR'S OWN ACTION, which must be acknowledged even when the plan
          it returned is identical — pressing Refresh and hearing nothing is what this answers. */}
      <p role="status" aria-live="polite" className="sr-only">{ack}</p>

      {error !== null && (
        <p role="alert" className="flex items-start gap-1.5 text-xs text-status-amber-fg">
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
          {error}
        </p>
      )}

      {/* THE SLOT IS RESERVED BEFORE THE CHART ARRIVES. This sits directly above Detach coverage,
          an armed destructive control, inside a 45vh scroller — 200 px of chart appearing at once
          moves what is under the operator's cursor. Three empty lanes is the common case. */}
      {view === null && error === null && (
        <div className="space-y-1.5" aria-hidden="true">
          <div className="h-3 w-48 rounded bg-muted/40" />
          {[0, 1, 2].map((i) => (
            <div key={i} className="min-h-10 py-1 flex items-center border-b border-border/50 last:border-0">
              <div className="h-8 w-full rounded bg-muted/30" />
            </div>
          ))}
          {/* THE STRIP IS PART OF WHAT THE SLOT RESERVES (PR 4). Without this row the detail strip's
              36 px arrived with the read, moving the battery-policy line and the lifecycle drawer's
              toggle below it — the same shift the strip's own reserved height exists to avoid,
              just relocated to the load. */}
          <div className="min-h-9 rounded border border-border/40" />
        </div>
      )}

      {/* A ROTATION WITH NO AIRCRAFT LEFT still answers 200 with empty lanes, and a legend
          decoding five colours over a ruler with no rows under it reads as a broken chart rather
          than an empty one. The panel above says what to DO about it, in amber. */}
      {view && view.lanes.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          No aircraft in this rotation to draw.
        </p>
      )}

      {view && view.lanes.length > 0 && domain && (
        <div className="space-y-1.5">
          {!view.forecastAvailable && (
            <p className="text-[11px] text-muted-foreground">
              No standing forecast to project — showing history and the current queue only.
            </p>
          )}

          {/* THE CHART GOES QUIET AFTER EVERY HANDOVER, AND HAS TO SAY WHY (#704).
              The row that is standing is the one that put the incumbent on station, so #592
              draws no pair off it — correctly — and everything past NOW is then empty. Without a
              line here the operator reads a bar that stops dead at the NOW rule and the aircraft
              flying home parked in STANDBY, which looks like a rotation ending rather than one
              mid-cycle. Muted, not amber: nothing is wrong. */}
          {view.forecastAvailable && view.forecastDrawn === false && (
            <p className="text-[11px] text-muted-foreground">
              The standing forecast has nothing left to draw — the next one is planned when this
              cycle finishes. Showing history and the current queue only.
            </p>
          )}

          {/* A SELF-SWAP CYCLE IS A DIFFERENT PICTURE, and saying so is not the same as warning
              about it (#564). The aircraft's own lane runs on through the cycle, so the uncovered
              stretch is not a hole to point at — it is the run of blocks where the aircraft is
              anywhere but the station, and at a 4-hour horizon that run is a few pixels wide.
              So the sentence carries the NUMBER instead of gesturing at the chart.

              GATED ON THE CYCLE BEING DRAWN, not on the rotation having one member. A completed
              cycle, one starting past the horizon, and a forecast naming a stood-down airframe
              all leave the chart with no cycle on it, and a caption describing one that is not
              there reads as a rendering fault.

              It also says nothing about whether this rotation was CONFIGURED with one aircraft or
              is a two-aircraft rotation that lost one — the panel above owns that distinction and
              warns in amber about the second. Muted, no triangle: this describes the mechanism,
              which is not by itself a fault.

              ONE TEXT NODE, not a sentence stitched from JSX children: splitting it around the
              expression is what turns "uncovered for 12m 20s each cycle" into three fragments in
              the DOM, which no text query — and no screen reader reading by node — sees whole. */}
          {view.selfSwapCycle === true && (
            <p className="text-[11px] text-muted-foreground">
              {'One aircraft, relieving itself: it returns to its own dock to be resupplied, and '
                + (view.selfSwapGapSeconds != null
                  ? `the station is uncovered for ${formatDuration(view.selfSwapGapSeconds)} each cycle.`
                  : 'the station is uncovered until it climbs back out.')}
            </p>
          )}

          <TimelineLegend showDecision={decisionActionable} />

          <TimelineChart
            view={view}
            domain={domain}
            timezone={timezone}
            selectedKey={selectedKey}
            onSelect={(block, key) => selectBlock(block, key, false)}
            decisionActionable={decisionActionable}
          />
          <TimelineBlockDetail
            selection={selection}
            timezone={timezone}
            expanded={false}
            decisionActionable={decisionActionable}
          />
        </div>
      )}
    </div>
  );
}
